import React, { useEffect, useState } from 'react';
import { Alert, Badge, Button, Col, Form, Modal, Nav, Row, Spinner, Table } from 'react-bootstrap';
import { Link, useNavigate } from 'react-router-dom';
import { ladderProblems, wavesFromAudit } from './ladderShape';
import CreateCodesPanel from './CreateCodesPanel';
import CodesPanel from './CodesPanel';
import EntriesPanel from './EntriesPanel';
import AuditPanel from './AuditPanel';
import serializeCsv from '../../utils/csv/exportCsv';
import { wavePath } from './wavePath';
import { getCampaignBudget, getCampaigns } from '../../apis/campaigns.api';
import { campaignOptionLabel, claimsCapCampaigns } from '../promotions/promos/CreatePromoModal';
import {
  UTC_INPUT_HINT,
  formatUtcWindow,
  isoToUtcInput,
  showUtcClock,
  showUtcDate,
  utcInputToIso,
  utcInputWithinIsoRange,
} from '../../utils/utcDateTime';

// SAMPLE A (Gwen's pick, 2026-08-19): the Waitlist tab is ONLY a list of
// waves. Click a wave and its detail opens: a Wave info card, then tabs —
// Invite Codes (create + usage progress) / Code Entries / Audit / Report
// (closed waves). Creating a wave is ONE popup carrying everything: the
// wave NAME (identity is a server-minted uuid, never shown), the Create
// Promo datetime pickers, and the tier bands — "everything in one shot"
// (her call, same day). The new wave's detail loads on create. A wave is
// one admission batch (cohort waves): it opens, players enter with its
// codes and earn coins, the close freezes its cohort, its report pays.

// A missing item (Atlantis down, a reward lookup refused) is NOT ready: the
// info card must render the outage, never throw on `undefined.alwaysWins`.
const READY = item => Boolean(item) && item.alwaysWins === true && item.disabled !== true;
const SELECTABLE = item => READY(item) && item.archived !== true;

const boundaryPassed = (active, value, fallback) => {
  const deadline = Date.parse(value);
  return !active || Number.isNaN(deadline) ? fallback : deadline <= Date.now();
};

const useBoundaryPassed = (active, value, fallback) => {
  const [, setVersion] = useState(0);
  useEffect(() => {
    const deadline = Date.parse(value);
    if (!active || Number.isNaN(deadline)) return undefined;
    let timer;
    const schedule = () => {
      const remaining = deadline - Date.now();
      if (remaining <= 0) {
        setVersion(version => version + 1);
        return;
      }
      timer = setTimeout(schedule, Math.min(remaining, 2147483647));
    };
    schedule();
    return () => clearTimeout(timer);
  }, [active, value]);
  return boundaryPassed(active, value, fallback);
};

const emptyBand = () => ({ fromCoins: '', toCoins: '', itemId: '' });

const toFormBands = tiers =>
  (tiers ?? []).map(tier => ({
    fromCoins: String(tier.fromCoins),
    toCoins: tier.toCoins === undefined || tier.toCoins === null ? '' : String(tier.toCoins),
    itemId: tier.itemId,
  }));

const toApiBands = bands =>
  bands.map(band => ({
    fromCoins: Number(band.fromCoins),
    ...(band.toCoins === '' ? {} : { toCoins: Number(band.toCoins) }),
    itemId: band.itemId,
  }));

/** Lists and cards show the UTC clock face the pickers edit. */
export const showWhen = showUtcClock;

/** Open waves that already sealed a Cap campaign — used to warn the picker. */
export const campaignsBoundToOpenWaves = (settings, waitlists = []) => {
  const bound = [];
  const seen = new Set();
  const add = (campaignId, waveName) => {
    if (!campaignId || seen.has(campaignId)) return;
    seen.add(campaignId);
    bound.push({ campaignId, waveName: waveName || 'an open wave' });
  };
  add(settings?.currentWave?.rfpCampaignId, settings?.currentWave?.name);
  for (const row of waitlists) {
    add(row?.currentWave?.rfpCampaignId, row?.currentWave?.name ?? row?.name ?? row?.promoName);
  }
  return bound;
};

/** Cap remaining + redeem-by, from the budget when present, else the campaign. */
export const campaignCapSummary = (campaign, budget) => {
  if (!campaign && !budget) return '';
  const limit = Number(budget?.solLimit ?? campaign?.solLimit);
  const remaining =
    budget?.remainingSol != null
      ? Number(budget.remainingSol)
      : Number.isFinite(limit) && budget?.allocatedSol != null
      ? limit - Number(budget.allocatedSol)
      : Number.isFinite(limit)
      ? limit
      : null;
  const expireAt = budget?.expireAt ?? campaign?.expireAt;
  const remainingText =
    remaining != null && Number.isFinite(remaining)
      ? `Cap remaining: ${remaining} SOL`
      : Number.isFinite(limit)
      ? `Cap ${limit} SOL`
      : null;
  const redeem = expireAt ? `redeem by ${showUtcDate(expireAt)}` : null;
  return [remainingText, redeem].filter(Boolean).join(' · ');
};

/**
 * ONE form for both moments (create = everything in one shot; edit = the
 * open wave's settings): the wave NAME, the Create Promo datetime pickers,
 * the Cap campaign (sealed at create), and the tier bands with the
 * shop-item dropdown. On create the body carries no waveId — the server
 * mints the uuid; on edit it carries the open wave's. A refused save
 * keeps the modal open with the server's own words; a landed save closes
 * it and reports the saved settings up.
 */
export const WaveFormModal = ({
  show,
  onHide,
  wave = null,
  items,
  onSave,
  saving,
  error,
  onSaved,
  promoWindow = null,
  openWaveCampaigns = [],
}) => {
  const editing = wave !== null;
  const [name, setName] = useState(wave?.name ?? '');
  const [startsAt, setStartsAt] = useState(isoToUtcInput(wave?.startsAt));
  const [closesAt, setClosesAt] = useState(isoToUtcInput(wave?.closesAt));
  const [rfpCampaignId, setRfpCampaignId] = useState(wave?.rfpCampaignId ?? '');
  const [campaigns, setCampaigns] = useState([]);
  const [campaignsState, setCampaignsState] = useState('idle');
  const [budget, setBudget] = useState(null);
  const [bands, setBands] = useState(wave?.tiers?.length ? toFormBands(wave.tiers) : [emptyBand()]);
  const filledBands = bands.filter(
    band => band.fromCoins !== '' || band.toCoins !== '' || band.itemId !== ''
  );
  // TIERS ARE REQUIRED (Gwen 2026-08-20): no wave without its ladder —
  // the service refuses too; this mirror just refuses earlier.
  const problems =
    filledBands.length === 0
      ? ['prize tiers are required: add at least one band']
      : ladderProblems(bands);
  const nameValid = name.trim().length > 0 && name.trim().length <= 60 && !name.includes(' · ');
  // datetime-local values are UTC wall clocks. Order and the promo-window
  // fit compare those instants, not the browser's zone.
  const startsIso = utcInputToIso(startsAt);
  const closesIso = utcInputToIso(closesAt);
  const hasSchedule = Boolean(startsIso && closesIso);
  const orderOk = !hasSchedule || Date.parse(closesIso) > Date.parse(startsIso);
  // The wave must FIT inside its promo's window (Gwen's rule, 2026-08-26):
  // create anytime — the promo need not be active yet — but the dates the
  // wave runs on belong to the promo's own range.
  const windowOk =
    utcInputWithinIsoRange(startsAt, promoWindow?.startTime, promoWindow?.endTime) &&
    utcInputWithinIsoRange(closesAt, promoWindow?.startTime, promoWindow?.endTime);
  const readyItems = items.filter(SELECTABLE);
  const campaignOk = editing || Boolean(rfpCampaignId);
  const selectedCampaign = campaigns.find(c => c.id === rfpCampaignId) ?? null;
  const boundOpenWave = openWaveCampaigns.find(row => row.campaignId === rfpCampaignId) ?? null;
  const capSummary = campaignCapSummary(selectedCampaign, budget);

  useEffect(() => {
    if (!show || editing) return undefined;
    let cancelled = false;
    setCampaignsState('loading');
    getCampaigns()
      .then(list => {
        if (cancelled) return;
        const options = claimsCapCampaigns(list);
        setCampaigns(options);
        setRfpCampaignId(id => (options.some(c => c.id === id) ? id : ''));
        setCampaignsState('ready');
      })
      .catch(() => {
        if (cancelled) return;
        setCampaigns([]);
        setRfpCampaignId('');
        setCampaignsState('error');
      });
    return () => {
      cancelled = true;
    };
  }, [show, editing]);

  useEffect(() => {
    if (!show || editing || !rfpCampaignId) {
      setBudget(null);
      return undefined;
    }
    let cancelled = false;
    getCampaignBudget(rfpCampaignId)
      .then(answer => {
        if (!cancelled) setBudget(answer ?? null);
      })
      .catch(() => {
        if (!cancelled) setBudget(null);
      });
    return () => {
      cancelled = true;
    };
  }, [show, editing, rfpCampaignId]);

  const setBand = (index, patch) =>
    setBands(previous => previous.map((row, i) => (i === index ? { ...row, ...patch } : row)));

  return (
    <Modal
      show={show}
      onHide={saving ? undefined : onHide}
      backdrop={saving ? 'static' : true}
      keyboard={!saving}
      centered
      size="lg"
    >
      <Modal.Header closeButton={!saving}>
        <Modal.Title>{editing ? wave.name : 'Create Wave'}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {error && (
          <Alert variant="danger" className="small">
            {error}
          </Alert>
        )}
        <Row className="g-2 mb-3">
          <Col md={4}>
            <Form.Label>Wave name</Form.Label>
            <Form.Control
              autoFocus={!editing}
              value={name}
              onChange={e => setName(e.target.value)}
              placeholder="Launch Wave 1"
              aria-label="wave name"
            />
          </Col>
          <Col md={4}>
            <Form.Label>Start time</Form.Label>
            <Form.Control
              type="datetime-local"
              value={startsAt}
              onChange={e => setStartsAt(e.target.value)}
              aria-label="start time"
            />
            <Form.Text muted>{UTC_INPUT_HINT}</Form.Text>
          </Col>
          <Col md={4}>
            <Form.Label>Cutoff (freeze scores)</Form.Label>
            <Form.Control
              type="datetime-local"
              value={closesAt}
              onChange={e => setClosesAt(e.target.value)}
              aria-label="end time"
            />
            <Form.Text muted>{UTC_INPUT_HINT}</Form.Text>
            <Form.Text muted>
              The deadline players see. Scores freeze here. Close + Grant is a later step.
            </Form.Text>
          </Col>
        </Row>
        {!orderOk && (
          <Alert variant="warning" className="small">
            The end time must come after the start time.
          </Alert>
        )}
        {!hasSchedule && (
          <Alert variant="warning" className="small">
            Set both the start time and the end time.
          </Alert>
        )}
        {name.includes(' · ') && (
          <Alert variant="warning" className="small">
            Wave names cannot contain the " · " separator.
          </Alert>
        )}
        {promoWindow?.startTime && promoWindow?.endTime && (
          <Form.Text muted>
            {`"${promoWindow.name}" runs ${formatUtcWindow(
              promoWindow.startTime,
              promoWindow.endTime
            )}. The wave must fit inside that range.`}
          </Form.Text>
        )}
        {!windowOk && (
          <Alert variant="warning" className="small">
            The wave dates fall outside the promo range.
          </Alert>
        )}
        {editing ? (
          <div className="mb-3">
            <Form.Label>Cap campaign</Form.Label>
            <div className="small">
              {wave.rfpCampaignId ? (
                <>
                  Sealed at create: <code>{wave.rfpCampaignId}</code>
                  {wave.rfpCampaignName ? ` · ${wave.rfpCampaignName}` : ''}
                </>
              ) : (
                <span className="text-muted">
                  No Cap campaign sealed on this wave (created before wave-level bind).
                </span>
              )}
            </div>
            <Form.Text muted>
              The grant uses this wave&apos;s campaign. It cannot be changed here.
            </Form.Text>
          </div>
        ) : (
          <Form.Group className="mb-3" controlId="waveRfpCampaignId">
            <Form.Label>Cap campaign</Form.Label>
            <Form.Select
              value={rfpCampaignId}
              onChange={e => setRfpCampaignId(e.target.value)}
              disabled={campaignsState === 'loading' || campaigns.length === 0}
              required
              aria-label="Cap campaign"
            >
              <option value="">
                {campaignsState === 'loading'
                  ? 'Loading campaigns…'
                  : campaignsState === 'error'
                  ? 'Could not load campaigns'
                  : 'Pick a claims-cap campaign'}
              </option>
              {campaigns.map(c => (
                <option key={c.id} value={c.id}>
                  {campaignOptionLabel(c)}
                </option>
              ))}
            </Form.Select>
            {capSummary && <div className="small mt-1">{capSummary}</div>}
            {campaignsState === 'ready' && campaigns.length === 0 && (
              <div className="mt-1">Create a campaign with cap counts = claims first</div>
            )}
            {boundOpenWave && (
              <Alert variant="warning" className="small mt-2 mb-0">
                This campaign is already bound to open wave &ldquo;{boundOpenWave.waveName}&rdquo;.
                Two open waves would share the same Cap.
              </Alert>
            )}
            <Form.Text muted>
              This campaign is sealed on this wave at create. The grant uses this wave&apos;s
              campaign — rebinding the promo later cannot change it for this wave.
            </Form.Text>
          </Form.Group>
        )}
        <div className="text-muted small mb-2">
          Prize tiers: how many Jelly deserve which reward. Bands must cover every Jelly count with
          no gaps and no overlaps, start at 0, one open-ended top band.
        </div>
        {bands.map((band, index) => (
          <Row className="g-2 mb-2" key={index}>
            <Col xs={3} md={2}>
              <Form.Control
                value={band.fromCoins}
                onChange={e => setBand(index, { fromCoins: e.target.value })}
                placeholder="from"
                aria-label={`band ${index + 1} from Jelly`}
              />
            </Col>
            <Col xs={3} md={2}>
              <Form.Control
                value={band.toCoins}
                onChange={e => setBand(index, { toCoins: e.target.value })}
                placeholder="and up"
                aria-label={`band ${index + 1} to Jelly`}
              />
            </Col>
            <Col>
              <Form.Select
                value={band.itemId}
                onChange={e => setBand(index, { itemId: e.target.value })}
                aria-label={`band ${index + 1} box item`}
              >
                <option value="">pick a reward from the shop items…</option>
                {items.map(item => (
                  <option key={item.itemId} value={item.itemId} disabled={!SELECTABLE(item)}>
                    {item.name ?? item.itemId}
                    {SELECTABLE(item) ? '' : ' (unavailable for new tiers)'}
                  </option>
                ))}
              </Form.Select>
            </Col>
            <Col xs="auto">
              <Button
                variant="outline-secondary"
                size="sm"
                onClick={() => setBands(previous => previous.filter((_, i) => i !== index))}
              >
                Remove
              </Button>
            </Col>
          </Row>
        ))}
        <Button
          variant="outline-secondary"
          size="sm"
          onClick={() => setBands(previous => [...previous, emptyBand()])}
        >
          Add tier
        </Button>
        {problems.length > 0 && (
          <Alert variant="warning" className="mt-3 mb-0 small">
            {problems.map(problem => (
              <div key={problem}>{problem}</div>
            ))}
          </Alert>
        )}
        {readyItems.length === 0 && items.length > 0 && (
          <Alert variant="danger" className="mt-3 mb-0 small">
            No ready items: every box under the promo is misconfigured. The tiers cannot save.
          </Alert>
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" disabled={saving} onClick={onHide}>
          Cancel
        </Button>
        <Button
          disabled={
            saving ||
            !nameValid ||
            !hasSchedule ||
            !orderOk ||
            !windowOk ||
            problems.length > 0 ||
            !campaignOk
          }
          onClick={async () => {
            const campaignId = editing ? wave.rfpCampaignId : rfpCampaignId;
            if (!editing && !campaignId) return;
            const saved = await onSave({
              ...(editing ? { waveId: wave.waveId } : {}),
              name: name.trim(),
              ...(startsIso ? { startsAt: startsIso } : {}),
              ...(closesIso ? { closesAt: closesIso } : {}),
              tiers: toApiBands(bands),
              ...(campaignId ? { rfpCampaignId: campaignId } : {}),
            });
            if (saved) {
              onHide();
              if (onSaved) onSaved(saved);
            }
          }}
        >
          {saving ? 'Saving…' : editing ? 'Save' : 'Create wave'}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

const ladderSummary = (tiers, nameFor = id => id) =>
  (tiers ?? [])
    .map(t => `${t.fromCoins}–${t.toCoins ?? '∞'} Jelly: ${nameFor(t.itemId)}`)
    .join(' · ');

export const closedCohortCount = wave => {
  const match = /(?:^|\s)(\d+) eligible \/ (\d+) excluded(?:$|\s)/.exec(wave?.summary ?? '');
  return match ? Number(match[1]) + Number(match[2]) : '—';
};

/** Sample A's front door: the waves list, nothing else. */
export const WavesList = ({
  promoNameFor = id => id,
  waitlistId = null,
  settings,
  items,
  itemsError,
  promoWindow = null,
  audit,
  entries,
  codes,
  onSaveSettings,
  saving,
  error,
  waitlists = [],
  mintOnly = false,
  lastWaveId = null,
}) => {
  const [showCreate, setShowCreate] = useState(false);
  const navigate = useNavigate();
  const openWave = settings?.currentWave ?? null;
  const admissionStarted = useBoundaryPassed(
    Boolean(openWave),
    openWave?.startsAt ?? openWave?.openedAt,
    true
  );
  const admissionEnded = useBoundaryPassed(Boolean(openWave), openWave?.closesAt, false);
  const closed = wavesFromAudit(audit);
  const countFor = (list, waveId) => list.filter(row => row.waveId === waveId).length;

  // VALIDATION (Gwen 2026-08-20): no WAITLIST promo, no wave — the tiers
  // are required and their rewards live under that promo. The button
  // disables and the hover says why, in her words.
  const readyItems = items.filter(SELECTABLE);
  // Atlantis problems SURFACE (Venom's re-review): an unreachable promo
  // service or a missing promo binding is its own message, never disguised
  // as "no promo yet".
  const promoGate =
    itemsError !== null && itemsError !== undefined
      ? itemsError
      : items.length === 0
      ? promoWindow
        ? `"${promoWindow.name}" has no prize items yet: add shop items to it in Promos; wave prizes come from them.`
        : 'Create a Waitlist promo in Promos first. Wave prizes come from its shop items.'
      : readyItems.length === 0
      ? 'No ready prize items: the WAITLIST promo boxes need exactly one always-win reward each.'
      : null;
  const createDisabled = openWave !== null || promoGate !== null;
  const createTitle =
    promoGate ??
    (openWave ? `Close + Grant "${openWave.name}" before opening the next wave` : undefined);

  // Mint-only operators cannot create or edit waves (POST /settings is 403
  // for them on waitlist-svc), so the control is not offered.
  const createButton = mintOnly ? null : (
    <span title={createTitle} className="d-inline-block">
      <Button
        disabled={createDisabled}
        style={createDisabled ? { pointerEvents: 'none' } : undefined}
        onClick={() => setShowCreate(true)}
      >
        Create wave
      </Button>
    </span>
  );

  const hasRows = openWave !== null || closed.length > 0;
  return (
    <>
      <div className="d-flex justify-content-between align-items-center my-3">
        <div className="text-muted small">
          {openWave && !admissionStarted
            ? `"${openWave.name}" is scheduled. Admissions have not started.`
            : openWave && !admissionEnded
            ? `"${openWave.name}" is admitting. One wave at a time.`
            : openWave
            ? `"${openWave.name}" cutoff passed. Scores are frozen. Anti-sybil, then Close + Grant before opening the next wave.`
            : promoGate ?? (hasRows ? 'No wave is admitting. Create the next one.' : '')}
        </div>
        {hasRows && createButton}
      </div>
      <div className="waitlist-panel">
        <h5>Waves</h5>
        {!hasRows ? (
          <div className="text-center py-5">
            <h5 className="mb-2">No waves yet</h5>
            <div className="text-muted small mb-3">
              A wave is one admission batch: it opens, players enter with codes and earn Jelly.
              <br />
              The cutoff freezes scores. Close + Grant pays from that snapshot. One wave at a time.
            </div>
            {promoGate && <div className="text-warning small mb-3">{promoGate}</div>}
            {createButton}
          </div>
        ) : (
          <>
            <Table hover responsive size="sm">
              <thead>
                <tr>
                  <th>Wave</th>
                  <th>Status</th>
                  <th>Start</th>
                  <th>Cutoff (freeze scores)</th>
                  <th>Entries</th>
                  <th>Codes</th>
                  <th>Result</th>
                </tr>
              </thead>
              <tbody>
                {openWave && (
                  <tr
                    style={{ cursor: 'pointer' }}
                    onClick={() => navigate(wavePath(waitlistId, openWave.waveId))}
                  >
                    <td className="fw-semibold">
                      {openWave.name}
                      {openWave.shopPromoId && (
                        <span
                          className="text-muted small ms-2"
                          title={`sealed to promo ${openWave.shopPromoId}`}
                        >
                          {promoNameFor(openWave.shopPromoId)}
                        </span>
                      )}
                      {lastWaveId && openWave.waveId === lastWaveId && (
                        <>
                          {' '}
                          <Badge bg="dark">Last wave</Badge>
                        </>
                      )}
                    </td>
                    <td>
                      {!admissionStarted ? (
                        <Badge bg="info">scheduled</Badge>
                      ) : admissionEnded ? (
                        <Badge bg="warning" text="dark">
                          cutoff
                        </Badge>
                      ) : (
                        <Badge bg="success">open</Badge>
                      )}
                    </td>
                    <td>{showWhen(openWave.startsAt ?? openWave.openedAt)}</td>
                    <td>{showWhen(openWave.closesAt)}</td>
                    <td>{entries.length}</td>
                    {/* the open row counts ITS codes, like the closed rows do:
                        the list is program-wide, so the raw length would add
                        every earlier wave's codes to this one */}
                    <td>{countFor(codes, openWave.waveId)}</td>
                    <td className="text-muted small">
                      {!admissionStarted
                        ? 'waiting to open'
                        : admissionEnded
                        ? 'scores frozen — anti-sybil, then Close + Grant'
                        : 'still earning'}
                    </td>
                  </tr>
                )}
                {closed.map(wave => (
                  <tr
                    key={wave.waveId}
                    style={{ cursor: 'pointer' }}
                    onClick={() => navigate(wavePath(waitlistId, wave.waveId))}
                  >
                    <td className="fw-semibold">
                      {wave.name}
                      {lastWaveId && wave.waveId === lastWaveId && (
                        <>
                          {' '}
                          <Badge bg="dark">Last wave</Badge>
                        </>
                      )}
                    </td>
                    <td>
                      <Badge bg="secondary">closed</Badge>
                    </td>
                    <td>—</td>
                    <td>{showWhen(wave.at)}</td>
                    <td>{closedCohortCount(wave)}</td>
                    <td>{countFor(codes, wave.waveId)}</td>
                    <td className="text-muted small">{wave.summary}</td>
                  </tr>
                ))}
              </tbody>
            </Table>
            <div className="text-muted small">
              Click a wave to work inside it. Cutoff freezes scores; Close + Grant pays and closes.
              One wave at a time; a closed wave never reopens.
            </div>
          </>
        )}
      </div>
      {showCreate && (
        <WaveFormModal
          promoWindow={promoWindow}
          show
          onHide={() => setShowCreate(false)}
          items={items}
          onSave={onSaveSettings}
          saving={saving}
          error={error}
          openWaveCampaigns={campaignsBoundToOpenWaves(settings, waitlists)}
          onSaved={saved => {
            const waveId = saved?.settings?.currentWave?.waveId ?? saved?.currentWave?.waveId;
            if (waveId) navigate(wavePath(waitlistId, waveId)); // the new wave loads
          }}
        />
      )}
    </>
  );
};

/** "No more waves after this one" — a full-admin switch. It writes
 *  immediately through onSetLastWave (the server accepts only the open wave,
 *  or the latest closed wave while no wave is open); disabled while a write is in flight. */
const LastWaveSwitch = ({ id, waveId, marked, busy, onSetLastWave, help = true }) => (
  <div>
    <Form.Check
      type="switch"
      id={id}
      label="Last wave"
      checked={marked}
      disabled={busy}
      onChange={() => onSetLastWave(waveId, !marked)}
    />
    {help && (
      <Form.Text className="text-muted">
        No more waves after this one. Players see &quot;Waves are closed&quot; after it is closed
        and granted.
      </Form.Text>
    )}
  </div>
);

/** The Wave info card: the wave's facts, the tier summary, and two distinct
 *  operator moments — Cutoff / Freeze scores (automatic at closesAt) vs
 *  Close + Grant (the two-step confirm). */
const WaveInfoCard = ({
  waveId,
  isOpen,
  wave,
  report,
  cohortCount,
  onEdit,
  onClose,
  closing,
  allowClose = true,
  allowEdit = true,
  allowFreeze = false,
  lastWaveId = null,
  canMarkLast = false,
  onSetLastWave,
  lastWaveBusy = false,
  lastWaveError = null,
  promoNameFor = id => id,
  items = [],
  itemsError = null,
  admissionStarted = true,
  admissionEnded = false,
}) => {
  const [armed, setArmed] = useState(false);
  // A refused last-wave write inside the confirm modal: the page-level Alert
  // sits behind the modal, so the same message (the page's `error`) is echoed
  // under the switch. Cleared on the next write, on success, and on close.
  const [lastWaveFailed, setLastWaveFailed] = useState(false);
  useEffect(() => {
    if (!armed) setLastWaveFailed(false);
  }, [armed]);
  const writeLastWave = async (id, value) => {
    setLastWaveFailed(false);
    const ok = await onSetLastWave(id, value);
    setLastWaveFailed(ok === false);
  };
  const itemNameById = new Map((items ?? []).map(item => [item.itemId, item.name]));
  const prizeNameFor = itemId => itemNameById.get(itemId) ?? itemId;
  const displayName = isOpen ? wave.name : report?.report?.name ?? waveId;
  // A wave row without a ladder (legacy or half-configured) is an empty
  // ladder here, never a crash: the card then says 'not set' and Close stays off.
  const tiers = (isOpen ? wave?.tiers : report?.report?.tiers) ?? [];
  // Promo-binding v2: the wave sealed its waitlist promo at creation —
  // the card names the bound promo so the operator always knows which
  // waitlist this wave pays under. The seal never moves.
  const sealedPromoId = isOpen ? wave?.shopPromoId : report?.report?.shopPromoId;
  const sealedCampaignId = isOpen ? wave?.rfpCampaignId : report?.report?.rfpCampaignId;
  const isLastWave = Boolean(lastWaveId) && waveId === lastWaveId;
  // Full admins only (hidden for mint-only, like Close + Grant), and only
  // for a wave the server will accept (eligibility is decided in the workspace).
  // An OPEN wave offers it only once cut off (the same admissionEnded signal
  // Close + Grant uses), never while it is still taking entries; the latest
  // sealed wave keeps it as the correction path after a close.
  const showLastWave =
    allowClose && canMarkLast && typeof onSetLastWave === 'function' && (!isOpen || admissionEnded);
  const hasLadder = tiers.length > 0;
  const ladderReady =
    hasLadder && tiers.every(tier => READY(items.find(item => item.itemId === tier.itemId)));
  // CUTOFF ≠ CLOSE (DEGEN-1475): closesAt freezes this wave's scores
  // automatically. Earn can continue for later waves. Close + Grant is the
  // later operator step (anti-sybil first). One wave at a time until Close.
  return (
    <div className="waitlist-panel">
      <div className="d-flex justify-content-between align-items-start flex-wrap gap-2">
        <h5 className="mb-0">
          {displayName}{' '}
          {!isOpen ? (
            <Badge bg="secondary">closed</Badge>
          ) : !admissionStarted ? (
            <Badge bg="info">scheduled</Badge>
          ) : admissionEnded ? (
            <Badge bg="warning" text="dark">
              cutoff: scores frozen
            </Badge>
          ) : (
            <Badge bg="success">open</Badge>
          )}{' '}
          {sealedPromoId && (
            <Badge bg="light" text="dark" title={`sealed to promo ${sealedPromoId}`}>
              {promoNameFor(sealedPromoId)}
            </Badge>
          )}{' '}
          {sealedCampaignId && (
            <Badge bg="light" text="dark" title={`sealed Cap campaign ${sealedCampaignId}`}>
              {sealedCampaignId}
            </Badge>
          )}
          {isLastWave && (
            <>
              {' '}
              <Badge bg="dark">Last wave</Badge>
            </>
          )}
        </h5>
        {isOpen && allowClose && (
          <>
            <Button
              variant="outline-danger"
              size="sm"
              disabled={closing || !ladderReady || !admissionEnded}
              title={
                itemsError
                  ? `Prize items did not load: ${itemsError}`
                  : !ladderReady
                  ? 'Every prize tier must use a ready prize item'
                  : !admissionEnded
                  ? 'Cutoff has not passed — scores are still live. Close + Grant comes after cutoff.'
                  : 'Close this wave and grant prizes from the frozen snapshot'
              }
              onClick={() => setArmed(true)}
            >
              {closing ? (
                <>
                  <Spinner as="span" animation="border" size="sm" className="me-1" /> Closing…
                </>
              ) : (
                'Close + Grant'
              )}
            </Button>
            <Modal show={armed} centered onHide={() => setArmed(false)}>
              <Modal.Header closeButton>
                <Modal.Title>Close + Grant &ldquo;{displayName}&rdquo;</Modal.Title>
              </Modal.Header>
              <Modal.Body>
                Cutoff already froze this wave&apos;s scores. Close + Grant pays prizes from that
                snapshot — not live Jelly — and closes the wave so the next one can open. Finish
                anti-sybil (export + freeze) first?
                {showLastWave && (
                  <div className="mt-3">
                    <LastWaveSwitch
                      id={`last-wave-confirm-${waveId}`}
                      waveId={waveId}
                      marked={isLastWave}
                      busy={lastWaveBusy}
                      onSetLastWave={writeLastWave}
                      help={false}
                    />
                    {lastWaveFailed && lastWaveError && (
                      <div className="small text-danger mt-1" role="alert">
                        {lastWaveError}
                      </div>
                    )}
                    <div className="small mt-1">
                      {isLastWave
                        ? 'This is the LAST wave. After the grant, players see "Waves are closed".'
                        : 'More waves will follow. Players see "a new wave is coming".'}
                    </div>
                  </div>
                )}
              </Modal.Body>
              <Modal.Footer>
                <Button variant="outline-secondary" size="sm" onClick={() => setArmed(false)}>
                  Cancel
                </Button>
                <Button
                  variant="danger"
                  size="sm"
                  disabled={closing || lastWaveBusy || !ladderReady || !admissionEnded}
                  onClick={() => {
                    if (!admissionEnded || !ladderReady) return;
                    setArmed(false);
                    onClose(waveId);
                  }}
                >
                  Confirm: close and grant prizes
                </Button>
              </Modal.Footer>
            </Modal>
          </>
        )}
      </div>
      {showLastWave && (
        <div className="mt-2">
          <LastWaveSwitch
            id={`last-wave-${waveId}`}
            waveId={waveId}
            marked={isLastWave}
            busy={lastWaveBusy}
            onSetLastWave={onSetLastWave}
          />
        </div>
      )}
      <Row className="g-3 mt-1">
        <Col sm={3}>
          <div className="fw-semibold">
            {isOpen ? showWhen(wave.startsAt ?? wave.openedAt) : showWhen(report?.report?.closedAt)}
          </div>
          <div className="text-muted small">{isOpen ? 'start' : 'closed at'}</div>
        </Col>
        <Col sm={3}>
          <div className="fw-semibold">
            {isOpen ? showWhen(wave.closesAt) : report?.report?.closedBy ?? '—'}
          </div>
          <div className="text-muted small">{isOpen ? 'cutoff (freeze scores)' : 'closed by'}</div>
        </Col>
        <Col sm={3}>
          <div className="fw-semibold">{cohortCount}</div>
          <div className="text-muted small">on the list</div>
        </Col>
        <Col sm={3}>
          <div className="fw-semibold">
            {isOpen
              ? !admissionStarted
                ? 'scheduled'
                : admissionEnded
                ? 'cutoff'
                : 'earning'
              : `${report?.report?.eligible ?? '…'} eligible / ${
                  report?.report?.excluded ?? '…'
                } excluded`}
          </div>
          <div className="text-muted small">result</div>
        </Col>
      </Row>
      <div className="d-flex gap-2 align-items-center mt-3 flex-wrap">
        <span className="text-muted small">prize tiers:</span>
        <span className="small font-monospace">
          {hasLadder
            ? ladderSummary(tiers, prizeNameFor)
            : isOpen
            ? 'not set: the wave cannot close without them'
            : '—'}
        </span>
        {isOpen && allowEdit && (
          <Button variant="outline-secondary" size="sm" onClick={onEdit}>
            Edit wave
          </Button>
        )}
      </div>
      {isOpen && admissionEnded && (allowClose || allowFreeze) && (
        <Alert variant="warning" className="small mt-3 mb-0">
          <div className="fw-semibold">Wave Is Cut Off!</div>
          <div>Next Thing To Do:</div>
          <ol className="mb-0 ps-3">
            {allowClose && <li>Run Anti-Sybil Report manually</li>}
            {(allowClose || allowFreeze) && <li>Freeze sus wallets</li>}
            {allowClose && <li>Close + Grant the prizes</li>}
          </ol>
        </Alert>
      )}
    </div>
  );
};

const WAVE_TABS = [
  { key: 'report', name: 'Report', closedOnly: true },
  { key: 'codes', name: 'Invite Codes' },
  { key: 'entries', name: 'Code Entries' },
  { key: 'audit', name: 'Audit' },
];

/** Sample A's detail view: Wave info + tabs. The tab rides the URL, so the
 *  breadcrumb (Waves > the wave > Invite Codes) always tells the truth. */
export const WaveWorkspace = ({
  waveId,
  tab,
  promoNameFor = id => id,
  settings,
  items,
  itemsError = null,
  promoWindow = null,
  audit,
  entries,
  codes,
  report,
  onLoadReport,
  onSaveSettings,
  onCloseWave,
  lastWaveId = null,
  onSetLastWave,
  lastWaveBusy = false,
  onMint,
  onSetEntryState,
  onBulkFreeze,
  onGrantJelly,
  onExportCsv,
  onExportWalletsCsv,
  waitlistId = null,
  saving,
  closing,
  error,
  mintOnly = false,
  canFreeze = !mintOnly,
}) => {
  const isOpen = settings?.currentWave?.waveId === waveId;
  const [showEdit, setShowEdit] = useState(false);
  const admissionStarted = useBoundaryPassed(
    isOpen,
    settings?.currentWave?.startsAt ?? settings?.currentWave?.openedAt,
    true
  );
  const admissionEnded = useBoundaryPassed(isOpen, settings?.currentWave?.closesAt, false);
  const activeTab = tab ?? (isOpen ? 'codes' : 'report');

  useEffect(() => {
    if (!isOpen && !closing && (report?.report?.waveId ?? null) !== waveId) onLoadReport(waveId);
  }, [waveId, isOpen, closing, report?.report?.waveId, onLoadReport]);

  // SNAPSHOT DROPS (Admin+Gwen 2026-08-24): one list, program-wide codes.
  // Every drop's workspace shows ALL codes and ALL members — the wave
  // stamp on rows is joined-during metadata, and the drop's report slices
  // by join time, not by membership.
  const waveCodes = codes;
  const waveEntries = entries;
  // Program-wide, not stamp-selected (review find, 2026-08-24): under
  // snapshot drops the stamps are attribution only — an older member
  // frozen during THIS drop's review must show here, and stamp selection
  // hid exactly those rows.
  const waveAudit = audit;
  const waveReport = (report?.report?.waveId ?? null) === waveId ? report : null;
  // Markable (the server's rule): the open wave, or the server-named
  // lastSealedWaveId while NO wave is open. Its lastWaveId is already the
  // effective one (null when a newer wave made the mark stale).
  const noOpenWave = !settings?.currentWave;
  const canMarkLast =
    isOpen ||
    (noOpenWave && Boolean(settings?.lastSealedWaveId) && settings.lastSealedWaveId === waveId);

  return (
    <>
      {/* An outage is its own message (never disguised as a broken ladder):
          the operator must tell "Atlantis is down" from "fix the tiers". */}
      {itemsError && (
        <Alert variant="danger" className="my-3">
          {itemsError}
        </Alert>
      )}
      <WaveInfoCard
        waveId={waveId}
        isOpen={isOpen}
        wave={settings?.currentWave}
        report={waveReport}
        cohortCount={isOpen ? waveEntries.length : waveReport?.rows?.length ?? '…'}
        onEdit={() => setShowEdit(true)}
        onClose={onCloseWave}
        closing={closing}
        allowClose={!mintOnly}
        allowEdit={!mintOnly}
        allowFreeze={canFreeze}
        lastWaveId={lastWaveId}
        canMarkLast={canMarkLast}
        onSetLastWave={onSetLastWave}
        lastWaveBusy={lastWaveBusy}
        lastWaveError={error}
        promoNameFor={promoNameFor}
        items={items}
        itemsError={itemsError}
        admissionStarted={admissionStarted}
        admissionEnded={admissionEnded}
      />
      <Nav variant="tabs" className="mt-3" activeKey={activeTab}>
        {WAVE_TABS.filter(entry => (entry.closedOnly ? !isOpen : true)).map(entry => (
          <Nav.Item key={entry.key}>
            <Nav.Link as={Link} to={wavePath(waitlistId, waveId, entry.key)} eventKey={entry.key}>
              {entry.name}
            </Nav.Link>
          </Nav.Item>
        ))}
      </Nav>
      {activeTab === 'codes' && (
        <>
          {/* Mint while scheduled too: a code admits nobody before startsAt,
              the service gates entry on the wave's own window (enter.ts),
              and operators hand codes out BEFORE the advertised start. */}
          {isOpen && !admissionEnded && <CreateCodesPanel onMint={onMint} />}
          <CodesPanel title="Code usage progress" codes={waveCodes} />
        </>
      )}
      {activeTab === 'entries' && (
        <EntriesPanel
          entries={waveEntries}
          onSetEntryState={isOpen && !closing && canFreeze ? onSetEntryState : undefined}
          onBulkFreeze={isOpen && !closing && canFreeze ? onBulkFreeze : undefined}
          onGrantJelly={isOpen && !closing && !mintOnly ? onGrantJelly : undefined}
          onDownloadCsv={onExportCsv}
          onDownloadWalletsCsv={onExportWalletsCsv}
          canExport={!mintOnly}
          pulseAntiSybil={isOpen && admissionEnded && !closing && !mintOnly}
        />
      )}
      {activeTab === 'audit' && <AuditPanel actions={waveAudit} />}
      {activeTab === 'report' && !isOpen && (
        <>
          {waveReport && !waveReport.error && !waveReport.loading ? (
            <WaveReportCard report={waveReport} items={items} audit={waveAudit} />
          ) : (
            <div className="waitlist-panel text-muted">
              {waveReport?.error ? (
                <>
                  The report could not load.
                  <Button variant="link" size="sm" onClick={() => onLoadReport(waveId)}>
                    Retry
                  </Button>
                </>
              ) : (
                <>
                  <Spinner as="span" animation="border" size="sm" className="me-2" />
                  Sealing the report… the close runs in the background and usually lands within ~20
                  seconds.
                </>
              )}
            </div>
          )}
        </>
      )}
      {showEdit && isOpen && (
        <WaveFormModal
          promoWindow={promoWindow}
          show
          onHide={() => setShowEdit(false)}
          wave={settings.currentWave}
          items={items}
          onSave={onSaveSettings}
          saving={saving}
          error={error}
          openWaveCampaigns={campaignsBoundToOpenWaves(settings)}
        />
      )}
    </>
  );
};

export const WaveReportCard = ({ report, items = [], audit = [] }) => {
  const grantRows = audit.filter(
    row =>
      row?.target === report?.report?.waveId && String(row?.action ?? '').startsWith('wave-grant')
  );
  const grant = grantRows.slice().sort((a, b) => (a.at < b.at ? 1 : -1))[0] ?? null;
  const granted = grant?.action === 'wave-grant-dispatched';
  if (!report) return null;
  const itemNames = new Map(items.map(item => [item.itemId, item.name]));
  const itemName = itemId => itemNames.get(itemId) ?? itemId;
  const exportReport = () => {
    const csv = serializeCsv({
      rows: report.rows,
      columns: [
        { key: 'cognitoSub', header: 'Account' },
        { key: 'walletId', header: 'Wallet' },
        { key: 'coins', header: 'Coins' },
        { key: 'itemId', header: 'Prize item' },
        { key: 'status', header: 'Status' },
        { key: 'reason', header: 'Reason' },
      ],
    });
    const url = window.URL.createObjectURL(new Blob([csv], { type: 'text/csv;charset=utf-8;' }));
    const link = document.createElement('a');
    link.href = url;
    link.download = `${report.report.waveId}-report.csv`;
    link.click();
    window.URL.revokeObjectURL(url);
  };
  return (
    <div className="waitlist-panel">
      <h5>
        {report.report.name ?? report.report.waveId} report{' '}
        <span className="text-muted small">
          closed {showWhen(report.report.closedAt)} by {report.report.closedBy}
        </span>
      </h5>
      {/* Waitlist cap + expiry: the campaign sealed for the prizes.
          Only what the report SEALED at close counts — never the promo's
          current binding, which Admin can re-bind after the fact while the
          granted boxes keep the campaign they were minted against. */}
      <div className="text-muted small mb-2">
        {granted ? 'Prizes pay into RFP campaign' : 'Prizes will pay into RFP campaign'}{' '}
        {report.report.rfpCampaignId ? (
          <code>{report.report.rfpCampaignId}</code>
        ) : (
          <span>not recorded — this wave closed before the report sealed its campaign</span>
        )}
        {granted ? (
          report.report.expiresAt && <> · redeem by {showWhen(report.report.expiresAt)}</>
        ) : grant?.action === 'wave-grant-dispatch-failed' ? (
          <> · grant failed — see audit</>
        ) : (
          <> once the grant runs</>
        )}
      </div>
      <Table hover responsive size="sm">
        <thead>
          <tr>
            <th>Tier band</th>
            <th>Box item</th>
            <th>Wallets</th>
          </tr>
        </thead>
        <tbody>
          {(report.report.tiers ?? []).map(tier => (
            <tr key={`${tier.fromCoins}-${tier.itemId}`}>
              <td>
                {tier.fromCoins} – {tier.toCoins ?? 'and up'} Jelly
              </td>
              <td>{itemName(tier.itemId)}</td>
              <td>{report.report.byItem?.[tier.itemId] ?? 0}</td>
            </tr>
          ))}
        </tbody>
      </Table>
      <Table hover responsive size="sm" className="mt-3">
        <thead>
          <tr>
            <th>Wallet</th>
            <th>Jelly</th>
            <th>Tier box</th>
            <th>Status</th>
          </tr>
        </thead>
        <tbody>
          {report.rows.map(row => (
            <tr key={row.cognitoSub ?? row.walletId}>
              <td className="font-monospace">{row.walletId}</td>
              <td>{row.coins}</td>
              <td>{row.itemId ? itemName(row.itemId) : '—'}</td>
              <td>
                {row.status === 'eligible' ? (
                  <Badge bg="success">eligible</Badge>
                ) : (
                  <Badge bg="danger">excluded · {row.reason}</Badge>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </Table>
      <div className="d-flex gap-2 align-items-center mt-2">
        <Button variant="outline-secondary" size="sm" onClick={exportReport}>
          Export CSV (sybil review)
        </Button>
        {grant === null ? (
          <span className="text-muted small">
            Prize grant: not dispatched. This wave closed before the close-and-grant chain; run the
            atlantis grant by hand.
          </span>
        ) : grant.action === 'wave-grant-dispatched' ? (
          <span className="text-success small">
            Prizes granted: dispatched to atlantis at {showWhen(grant.at)}. Winners claim on the
            /vip page. This report is final; frozen wallets stay excluded from every future wave
            until unfrozen.
          </span>
        ) : grant.action === 'wave-grant-skipped' ? (
          <Alert variant="warning" className="small my-0 py-1">
            The close finished but no grant function is configured. Run the atlantis grant by hand.
          </Alert>
        ) : (
          <Alert variant="danger" className="small my-0 py-1">
            Prize grant dispatch FAILED. The queue retries it; if this stays, check the close DLQ
            before granting by hand.
          </Alert>
        )}
      </div>
    </div>
  );
};
